import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface SearchItem { id: string; name: string; }

interface Props {
  items: SearchItem[];
  value: string;       // selected id as string, '' = nothing selected
  onChange: (id: string) => void;
  placeholder?: string;
  minWidth?: number;
  controlHeight?: number;
  disabled?: boolean;
  variant?: 'default' | 'order';
  ariaLabel?: string;
}

let _portalCounter = 0;

const SearchSelect: React.FC<Props> = ({ items, value, onChange, placeholder, minWidth, controlHeight, disabled = false, variant = 'default', ariaLabel }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const [activeIndex, setActiveIndex] = useState(-1);
  const listId = useId();
  const orderStyle = variant === 'order';
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const portalId = useRef(`ss-portal-${++_portalCounter}`).current;

  const selected = items.find(i => i.id === value);

  const calcPos = useCallback(() => {
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      const width = Math.max(r.width, minWidth ?? 0);
      setPos(orderStyle ? {
        top: window.innerHeight - r.bottom < 222 && r.top > window.innerHeight - r.bottom ? Math.max(8, r.top - 222) : r.bottom + 2,
        left: Math.max(8, Math.min(r.left, window.innerWidth - Math.min(width, window.innerWidth - 16) - 8)),
        width: Math.min(width, window.innerWidth - 16),
      } : { top: r.bottom + 2, left: r.left, width });
    }
  }, [minWidth, orderStyle]);

  const handleOpen = () => {
    if (disabled) return;
    calcPos();
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 10);
  };
  const handleClose = () => { setOpen(false); setQuery(''); setActiveIndex(-1); };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current && !wrapRef.current.contains(target)) {
        const portal = document.getElementById(portalId);
        if (portal && !portal.contains(target)) handleClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, portalId]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => calcPos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll); };
  }, [open, calcPos]);

  const filtered = query.trim()
    ? items.filter(i => i.name.toLowerCase().includes(query.toLowerCase()))
    : items;

  const focusControl = () => window.requestAnimationFrame(() => wrapRef.current?.querySelector<HTMLElement>('[role="combobox"]')?.focus());
  const select = (item: SearchItem) => { onChange(item.id); handleClose(); if (orderStyle) focusControl(); };
  const clear = (e: React.MouseEvent) => { e.stopPropagation(); onChange(''); setQuery(''); if (orderStyle) focusControl(); };
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!orderStyle || disabled || event.target !== event.currentTarget) return;
    if (event.key === 'Escape') { event.preventDefault(); handleClose(); focusControl(); }
    else if (event.key === 'Tab') handleClose();
    else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) handleOpen();
      setActiveIndex(index => event.key === 'ArrowDown'
        ? Math.min(index + 1, filtered.length - 1)
        : Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (!open) handleOpen();
      else if (filtered[activeIndex]) select(filtered[activeIndex]);
    }
  };
  useEffect(() => {
    if (open && activeIndex >= 0)
      document.getElementById(`${listId}-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex, listId]);

  const resolvedHeight = controlHeight ?? 30;
  const baseStyle: React.CSSProperties = {
    border: '1px solid var(--crm-border)', borderRadius: '10px', padding: '5px 8px',
    width: '100%', boxSizing: 'border-box', fontSize: '13px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)',
    minHeight: `${resolvedHeight}px`,
    height: `${resolvedHeight}px`,
    opacity: disabled ? 0.65 : 1,
  };

  return (
    <div ref={wrapRef} className={orderStyle ? 'crm-item-quantity__search' : undefined} style={{ position: 'relative', width: '100%' }}>
      {selected && !open ? (
        <div
          className={orderStyle ? 'crm-item-quantity__search-control' : undefined}
          role={orderStyle ? 'combobox' : undefined}
          aria-label={ariaLabel}
          aria-expanded={orderStyle ? open : undefined}
          aria-disabled={orderStyle ? disabled : undefined}
          aria-controls={orderStyle && open ? listId : undefined}
          tabIndex={orderStyle && !disabled ? 0 : undefined}
          onKeyDown={onKeyDown}
          style={{ ...baseStyle, display: 'flex', alignItems: 'center', gap: '4px', cursor: disabled ? 'default' : 'text' }}
          onClick={handleOpen}
        >
          <span title={selected.name} className={orderStyle ? 'crm-item-quantity__selected-name' : undefined} style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.name}</span>
          {!disabled && <button type="button" onClick={clear} aria-label={ariaLabel ? `Очистити: ${ariaLabel}` : undefined}
            className={orderStyle ? 'crm-item-quantity__clear' : undefined}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '16px', padding: '0 2px', lineHeight: 1, borderRadius: '999px' }}>
            ×
          </button>}
        </div>
      ) : (
        <input
          className={orderStyle ? 'crm-item-quantity__search-control' : undefined}
          role={orderStyle ? 'combobox' : undefined}
          aria-label={ariaLabel}
          aria-autocomplete={orderStyle ? 'list' : undefined}
          aria-expanded={orderStyle ? open : undefined}
          aria-controls={orderStyle && open ? listId : undefined}
          aria-activedescendant={orderStyle && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          onKeyDown={onKeyDown}
          ref={inputRef}
          style={baseStyle}
          value={query}
          placeholder={placeholder ?? '🔍 Пошук...'}
          disabled={disabled}
          onChange={e => { setQuery(e.target.value); setActiveIndex(-1); if (!open) { calcPos(); setOpen(true); } }}
          onFocus={() => { if (!open) { calcPos(); setOpen(true); } }}
        />
      )}

      {open && createPortal(
        <div id={portalId} className={orderStyle ? 'crm-item-quantity__options' : undefined} style={{
          position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
          zIndex: 99999, background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', borderRadius: '6px',
          boxShadow: '0 6px 20px rgba(0,0,0,.18)', maxHeight: orderStyle ? Math.max(0, Math.min(220, window.innerHeight - pos.top - 8)) : 220, overflowY: 'auto',
        }}>
          <div id={listId} role={orderStyle ? 'listbox' : undefined} aria-label={ariaLabel}>
          {filtered.length === 0
            ? <div style={{ padding: '10px 12px', color: 'var(--crm-text-muted)', fontSize: '13px' }}>Нічого не знайдено</div>
            : filtered.map((i, index) => (
              <div key={i.id} id={`${listId}-${index}`} role={orderStyle ? 'option' : undefined}
                aria-selected={orderStyle ? filtered[activeIndex]?.id === i.id : undefined}
                onMouseDown={event => { if (orderStyle) event.preventDefault(); select(i); }}
                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid var(--crm-table-row-border)', color: 'var(--crm-text)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--crm-surface-alt)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                {i.name}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SearchSelect;
